import React, {useMemo, useState} from 'react';
import {Empty, Input, Modal, Select} from "antd";
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {useDebounce} from "react-use";
import UserImageCircle from "../../../components/UserImageCircle";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {useAPI} from "../../../config/API";

const { Search } = Input;

const NotFoundContent = ({search, isLoading, onInvite}) => {

    const {
        team,
    } = useTalentTeam();

    const existingMemberFound = useMemo(
        () => {
            let positionEmails = TB.map(team.positions, 'user.email');

            let inviteEmails = TB.map([...(team.pending_invites || []), ...(team.rejected_invites || [])], 'recipient.email');
            inviteEmails = TB.filter(inviteEmails, email => !!email);

            return [...inviteEmails, ...positionEmails].includes(search)
        },
        [team?.positions,team.pending_invites, search],
    );


    const isValidEmail = (email) => {
        return String(email)
            .toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    };
    const EmptyDescription = useMemo(() => {
        if (search && isValidEmail(search)) {
            if (existingMemberFound) {
                return "Member Already Invited"
            }
            return  (
                <p className="px-10 mx-auto whitespace-pre text-center">
                    {"Account not found for\n"}<span className="text-info text-underline">{search}</span>.
                </p>
            )
        }
        return "No users found"
    }, [search, existingMemberFound]);

    if(search?.length < 3) return null;

    if(!existingMemberFound && isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-3">
                <LoadingSpinner size="sm"/>
            </div>
        )
    }

    return (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EmptyDescription}>
            {search && isValidEmail(search) && (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <button className="btn btn-sm btn-dark" disabled={existingMemberFound} onClick={onInvite}>
                        <p className="text-xs text-white">
                            Invite Member
                        </p>
                    </button>
                </div>
            )}
        </Empty>
    )
}

function TalentManagerFillTeamMemberModal(props) {
    const {auth} = useAuth();
    const [modelOpen, setModalOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState(undefined);
    const [debounceLoading, setDebounceLoading] = useState(false);
    const [debounceInput, setDebounceInput] = useState('');
    const [existingMember, setExistingMember] = useState(undefined);

    const [isSearchReady, cancel] = useDebounce(
        () => {
            setDebounceInput(search);
        },
        400, [search]
    );
    const {
        team,
        isLoading,
        inviteTalentMember,
        inviteExternalMember,
        addTalentMember,
        isUpdating
    } = useTalentTeam();

    const {
        data,
        isFetching,
        error,
        refetch
    } = useAPI(['searchAllTalentForMemberInvite', {team, search: debounceInput, talent_discipline_id: props.talentDiscipline?.id}], {
        enabled: modelOpen && (props.talentDiscipline?.id || (search?.length > 0 && debounceInput?.length >= 3)) && !isUpdating && existingMember === undefined,
    })


    const [isLoadingReady, cancelLoading] = useDebounce(
        () => {
            setDebounceLoading(isFetching);
        },
        400, [isFetching]
    );


    const searchOptions = useMemo(() => {
        if (!props.talentDiscipline?.id && !search?.length) return [];

        return TB.map(data || [], option => ({
            value: option.id,
            display: option.fullName?.(),
            label: (
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                        <UserImageCircle user={option} size={40}/>
                        <p className="text-sm">{option.fullName?.()}</p>
                    </div>
                    {option.location?.city && (
                        <p className="text-xxs text-info text-right whitespace-nowrap">
                            {option.location?.city}, {option.location.state}
                        </p>
                    )}
                </div>
            ),
        }));
    }, [props.talentDiscipline?.id, search,data, isFetching]);


    const toggleSearchModal = async() => {
        await setModalOpen(!modelOpen)
    }

    const onInviteExternal = () => {
        setSearchInput(search);
        return setExistingMember(false)
    }

    const onMemberSelected = (newValue) => {
        let selected = TB.find(data || [], ['id', newValue])
        if (selected) {
            setSearchInput(selected.fullName?.())
            return setExistingMember(selected)
        }
    }

    const onConfirmInvite = async() => {
        let result;
        if (existingMember) {
            result = await inviteTalentMember(existingMember)
        }else {
            result = await inviteExternalMember(searchInput)
        }
        if (result) return toggleSearchModal();
    }

    /**
     * Supplier teams are staffed only from the agency's own resource pool, and the
     * invite endpoint rejects them outright — so on those teams the search is
     * scoped to that agency and direct add is the only way to place someone.
     */
    const isSupplierTeam = !!team?.is_supplied_by_client;

    /**
     * Direct add needs a real, active Wripple account, so it isn't offered for the
     * external-email path or for talent that exists but isn't active — those can
     * only ever be invited.
     */
    const canAddDirectly = auth.canManageTalent() && !!existingMember && !!existingMember.is_active;

    /** Place the talent on the team immediately, skipping the invite handshake. */
    const onConfirmAdd = async() => {
        // Seed the position title from the talent's own job title, matching how
        // AdminAddTeamMemberModal defaults it.
        const result = await addTalentMember(existingMember.id, existingMember.job_title);
        if (result) return toggleSearchModal();
    }
    return (
        <React.Fragment>
            <button className="btn btn-primary" onClick={toggleSearchModal} disabled={!team.can_manage || isUpdating || isLoading}>
                <i className="fas fa-plus mr-2 text-white" /> Find Talent Member
            </button>
            <Modal
                destroyOnClose={true}
                open={modelOpen}
                afterClose={async () => {
                    await TB.queryClient.invalidateQueries(['searchAllTalentForMemberInvite', {team, search: debounceInput, talent_discipline_id: props.talentDiscipline?.id}])
                    setSearchInput(undefined)
                    setSearch("")
                    setExistingMember(undefined)

                }}
                closable={false}
                title={null}
                footer={null}
            >
                <div className="talent-team-member-invite">

                    <div className="self-stretch flex flex-col items-stretch gap-4">
                        <div className={TB.mapClass("h-[16px] mt-[-24px] mx-[-24px]", {
                            "bg-danger": existingMember === false || existingMember?.is_active === false,
                            "bg-gray": existingMember === undefined,
                            "bg-success": existingMember && existingMember?.is_active
                        })}></div>
                        <p className="text-2xl font-medium">
                            {existingMember === undefined && "Find & Invite Team Member"}
                            {existingMember === false && "Invited Team Member Not on Wripple"}
                            {existingMember && existingMember.is_active && "Team Member Active"}
                            {existingMember && !existingMember.is_active && "Invited Team Member Not Active"}
                        </p>
                    </div>

                    <hr className="my-4 mx-[-24px]"/>

                    {existingMember === undefined && (
                        <React.Fragment>
                            <p className="mb-4">
                                Search for potential team members matching the
                                role <span className="text-primary font-semibold">{props.talentDiscipline?.name}</span>{' '}
                                {isSupplierTeam
                                    ? "within this agency's talent resources"
                                    : 'on Wripple'} by typing their name or email.
                            </p>
                            {isSupplierTeam && (
                                <p className="mb-4 text-xs text-info">
                                    This team is supplied by an agency, so only that agency's own
                                    talent resources are searched — not all talent on Wripple.
                                </p>
                            )}
                            <Select
                                style={{width: '100%'}}
                                name="search_users"
                                placeholder="Search by Name or Email"
                                maxLength={255}
                                showSearch
                                options={searchOptions}
                                optionLabelProp="display"
                                defaultActiveFirstOption={false}
                                filterOption={false}
                                disabled={isUpdating}
                                value={searchInput}
                                onSearch={setSearch}
                                onChange={onMemberSelected}
                                notFoundContent={(
                                    <NotFoundContent
                                        search={search}
                                        onInvite={onInviteExternal}
                                        isLoading={search !== debounceInput || isFetching || debounceLoading}/>
                                )}
                                loading={isFetching || debounceLoading}
                            />


                        </React.Fragment>
                    )}

                    {existingMember && (existingMember.is_active ? (
                        (
                            <div className="flex flex-col gap-3">
                                <p>
                                    We have found <span className="text-[#5f3db1]">{searchInput}</span> active
                                    on the Wripple platform. The following information is associated with this person:
                                </p>

                                <p >Name: <b>{existingMember.fullName()}</b></p>

                                <p >
                                    Location: <b>{existingMember.location?.city}, {existingMember.location?.state}</b>
                                </p>
                                <p >Roles on Wripple:</p>

                                <ul>
                                    {existingMember.disciplines?.length > 0 ? existingMember.disciplines.map((role, rI) => (
                                        <span className="text-[#707683] font-semibold" key={role.id}>
                                            {role.discipline.name}{rI < existingMember.disciplines.length - 1 && <span>,&nbsp;</span>}
                                        </span>
                                    )) : <span>None</span>}
                                </ul>
                                <p>
                                    You can either
                                    invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to
                                    join the team, or add them to the team directly.
                                </p>
                                <p className="text-xs text-info">
                                    Adding them places them on the roster right away — no invite
                                    is sent and no acceptance is required.
                                </p>
                            </div>
                        )
                    ) : (
                        <div className="flex flex-col gap-3">
                            <p >
                                We found <span className="text-[#5f3db1] font-semibold">{searchInput}</span> but they are not
                                currently active on the Wripple Platform. Please confirm that you want to send an email to
                                invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to join the team.
                            </p>
                            <p >
                                We will notify them that they have been invited to the team.
                            </p>
                        </div>
                    ))}
                    {existingMember === false && (

                        <div className="flex flex-col gap-3">
                            <p >
                                We did not find <span className="text-[#5f3db1] font-semibold">{searchInput}</span> on the Wripple
                                Platform. Please confirm that you want to send an email to
                                invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to join the team.
                            </p>
                            <p>
                                They will first need to apply to join Wripple and complete our talent vetting process.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-row gap-3 justify-end mt-[50px]">

                        <button className="btn btn-outline-dark w-[100px]"
                                disabled={isUpdating}
                                onClick={toggleSearchModal}>
                            Cancel
                        </button>

                        {/* The invite endpoint rejects supplier teams, so on those
                            only the direct-add path is offered. */}
                        {existingMember !== undefined && !isSupplierTeam && (
                            <button className="btn btn-primary" disabled={isUpdating}
                                    onClick={onConfirmInvite}>
                                Confirm Invite
                            </button>
                        )}

                        {canAddDirectly && (
                            <button className="btn btn-dark" disabled={isUpdating}
                                    onClick={onConfirmAdd}>
                                Add to Team
                            </button>
                        )}
                    </div>
                </div>

            </Modal>
        </React.Fragment>
    );
}

export default TalentManagerFillTeamMemberModal;